
import Vue from "vue";
import Component from "vue-class-component";
import { Action } from "vuex-class";
import toast from "@/functions/toast.function";
import { Editor, EditorContent, Extension } from "@tiptap/vue-2";
import { Plugin, PluginKey } from "@tiptap/pm/state";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Placeholder from "@tiptap/extension-placeholder";
import { Watch } from "vue-property-decorator";
import moment from "moment";
import { capitalize } from "@vue/runtime-dom";
import { SalesToken } from "@/store/school/types";
import Cropper from "cropperjs";

function getCookie(cname) {
  let name = cname + "=";
  let decodedCookie = decodeURIComponent(document.cookie);
  let ca = decodedCookie.split(";");
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == " ") {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return undefined;
}

@Component<RegisterJobFair>({
  components: {
    EditorContent,
  },
  beforeRouteEnter(to, from, next) {
    const { token } = to.query;
    if (
      !token ||
      typeof token !== "string" ||
      !getCookie("g-recaptcha-response")
    ) {
      toast.warning("Sales Token diperlukan");
      return next({ name: "verification-school" });
    } else {
      return next();
    }
  },
  directives: {
    mask: {
      inserted(el, { arg, value }) {
        const input = el as HTMLInputElement & { showPicker: () => void };

        const host = document.createElement("label");
        const style = getComputedStyle(el);
        const paddingLeft = parseFloat(style.paddingLeft);
        const placeholder = el.getAttribute("placeholder") || "";
        const inputs = document.querySelectorAll("input");
        const index = Array.from(inputs).findIndex((e) => e == el);
        const id = el.id || `input${index}`;

        el.id = id;
        el.after(host);

        host.htmlFor = id;
        host.style.position = "absolute";
        host.style.top = `${el.scrollTop + el.clientHeight + 5}px`;
        host.style.left = `${el.scrollLeft + paddingLeft + 5}px`;
        host.style.width = `${el.clientWidth - 35}px`;
        host.style.height = `${el.clientHeight}px`;
        host.style.padding = style.padding;

        host.addEventListener("click", () => input.showPicker());
        const handler = (ev?) => {
          const { format } = value as Partial<{ format: string }>;
          const date = moment(input.value);
          const valid = date.isValid();

          switch (arg) {
            case "date": {
              host.textContent = valid ? date.format(format) : placeholder;
              if (valid) {
                host.style.background = style.background;
              } else {
                host.style.removeProperty("background");
              }
              break;
            }
            case "time": {
              host.style.background = style.background;
              host.textContent = input.value;
              break;
            }
          }
        };
        el.addEventListener("input", handler);
        handler();
      },
    },
  },
})
export default class RegisterJobFair extends Vue {
  @Action("getCity", { namespace: "admin" })
  private getCity!: ServiceCall<Respon<Record<string, any>[]>>;
  @Action("getProvince", { namespace: "admin" })
  private getProvince!: ServiceCall<Respon<Record<string, any>[]>>;
  @Action("getCountry", { namespace: "admin" })
  private getCountry!: ServiceCall<Respon<Record<string, any>[]>>;
  @Action("registerJobfairSales", { namespace: "admin" })
  private registerJobfairSales!: ServiceCall<Respon<string>>;
  @Action("schoolRequestValidateToken", { namespace: "school" })
  schoolRequestValidateToken!: ServiceCall<
    Respon<SalesToken>,
    { sales_token: string }
  >;

  // Form steps and state
  private step = 1;
  private domainAvailable = false;
  private domainAvailableText = "";

  // Event information
  private eventName = "";
  private subdomain = "";
  private description = "";
  private startDate = "";
  private endDate = "";
  private formattedStart = "";
  private formattedEnd = "";
  private startTime = "08:00";
  private endTime = "17:00";
  private eventTypes = Object.entries({
    SCHOOL: 1,
    ORGANIZATION: 2,
    SCHOOL_INTERNAL: 3,
    COMPANY: 4,
  }).map(([label, id]) =>
    Object({
      label: capitalize(label.toLocaleLowerCase()).replaceAll("_", " "),
      id,
    })
  );
  private eventType = "";

  // Location information
  private provinces: Array<any> = [];
  private cities: Array<any> = [];
  private city: any = null;
  private province: any = null;
  private address = "";

  // Organizer information

  private organizers = Array<SalesToken["admin"] & { phone: string }>();
  private organizerName = "";
  private organizerEmail = "";
  private organizerPhone = "";
  private imageCropperLogo = false;
  private organizerLogo: Blob | null = null;
  private organizerLogoPreview: null | string = null;

  salesToken: SalesToken | null = null;

  // PIC information
  private pics = [
    {
      name: "",
      position: "",
      email: "",
      phone: "",
    },
  ];
  private pic = "";

  // Banner information
  private banner: any = null;
  private bannerPreview: any = null;
  private editor: Editor | null = null;
  private imageCropper = false;
  private cropper: Cropper | null = null;

  mounted() {
    const { token } = this.$route.query;
    if (typeof token == "string") {
      this.schoolRequestValidateToken({
        params: { sales_token: token },
        success: ({ data: { data } }) => {
          this.salesToken = data;
          this.organizers = [{ ...data.admin, phone: "" }];
        },
      });
    }

    this.initializeEditor();

    this.getCountry({
      success: ({ data: { data } }) => {
        const indo = data.find(
          ({ translation }) =>
            String(translation).toLocaleLowerCase() === "indonesia"
        );
        if (indo) {
          this.getProvince({
            params: { country: indo.id },
            success: ({ data: { data } }) => (this.provinces = data),
          });
        }
      },
    });
  }

  beforeDestroy() {
    this.editor?.destroy();
  }

  saveCropped() {
    this.cropper
      ?.getCropperSelection()
      ?.$toCanvas()
      .then((canvas) => {
        const mime = "image/png";
        this.organizerLogoPreview = canvas.toDataURL(mime);
        canvas.toBlob((v) => (this.organizerLogo = v), mime, 1);
      });
  }
  initCropper(e: Event & { target: HTMLInputElement }) {
    const file = e.target.files?.[0];

    if (!file) return;

    if (!this.cropper) {
      this.cropper = new Cropper(new Image(), { container: "#cropper" });
    }

    console.log(file);

    const img = this.cropper.getCropperImage();
    if (img) {
      img.$image.src = URL.createObjectURL(file);
    }

    const canvas = this.cropper.getCropperCanvas();
    if (canvas) {
      canvas.style.width = "180px";
      canvas.style.height = "180px";
    }
    const selection = this.cropper.getCropperSelection();
    if (selection) {
      selection.initialAspectRatio = 1 / 1;
      selection.initialCoverage = 0.8;
      selection.aspectRatio = 1 / 1;
      selection.$center();
      // selection?.$resize("resize", undefined, undefined, 1 / 1);
    }
  }

  private initializeEditor() {
    this.editor = new Editor({
      content: this.description,
      extensions: [
        StarterKit,
        Underline,
        TextAlign.configure({
          types: ["heading", "paragraph"],
          alignments: ["left", "center", "right", "justify"],
          defaultAlignment: "left",
        }),
        Placeholder.configure({
          placeholder: "Tulis deskripsi acara di sini...",
        }),
        Extension.create({
          name: "counter-limit",
          addProseMirrorPlugins() {
            return [
              new Plugin({
                key: new PluginKey("characterCount"),
                filterTransaction: (transaction, state) => {
                  if (!transaction.docChanged) {
                    return true;
                  }

                  const newSize = transaction.doc.textContent.length;
                  const limit = 500;
                  const isPaste = transaction.getMeta("paste");

                  if (newSize <= limit) {
                    return true;
                  } else if (isPaste) {
                    const pos = transaction.selection.$head.pos;
                    const over = newSize - limit;
                    const from = pos - over;
                    const to = pos;

                    transaction.deleteRange(from, to);

                    return true;
                  } else {
                    return false;
                  }
                },
              }),
            ];
          },
        }),
      ],
      onUpdate: () => {
        this.description = this.editor?.getHTML() || "";
      },
      onFocus: () => {
        if (this.editor?.isEmpty) {
          this.editor.commands.setContent("<p></p>");
        }
      },
    });
  }

  // Editor methods
  private focusEditor() {
    this.editor?.commands.focus();
  }

  private toggleBold() {
    this.editor?.chain().focus().toggleBold().run();
  }

  private toggleItalic() {
    this.editor?.chain().focus().toggleItalic().run();
  }

  private toggleUnderline() {
    this.editor?.chain().focus().toggleUnderline().run();
  }

  private toggleBulletList() {
    this.editor?.chain().focus().toggleBulletList().run();
  }

  private toggleOrderedList() {
    this.editor?.chain().focus().toggleOrderedList().run();
  }

  private setTextAlign(align: string) {
    this.editor?.chain().focus().setTextAlign(align).run();
  }

  // PIC management
  private addPIC() {
    this.pics.push({
      name: "",
      position: "",
      email: "",
      phone: "",
    });
    this.$nextTick(() => {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: "smooth",
      });
    });
  }

  private removePIC(index: number) {
    if (this.pics.length > 1) {
      this.pics.splice(index, 1);
    } else {
      toast.warning("Minimal harus ada 1 PIC");
    }
  }

  @Watch("banner")
  private onTrigger() {
    if (this.banner) this.bannerPreview = URL.createObjectURL(this.banner);
    else this.bannerPreview = null;
  }

  // Computed properties
  get wordCount(): number {
    return this.editor ? this.editor.getText().length : 0;
  }

  // Utility methods
  private onlyNumber(event: KeyboardEvent) {
    const keyCode = event.keyCode || event.which;
    if (keyCode !== 43 && (keyCode < 48 || keyCode > 57)) {
      event.preventDefault();
    }
  }

  private getOrganizerData() {
    const foundOrganizer = this.organizers.find((org) =>
      org.name.toLowerCase().includes(this.organizerName.toLowerCase())
    );
    console.log("button klik");

    if (foundOrganizer) {
      this.organizerName = foundOrganizer.name;
      this.organizerEmail = foundOrganizer.email;
      this.organizerPhone = foundOrganizer.phone;
      toast.success("Data organizer berhasil dimuat");
    } else {
      this.organizerEmail = `contact@${this.organizerName
        .toLowerCase()
        .replace(/\s+/g, "")}.com`;
      this.organizerPhone =
        "08" + Math.floor(100000000 + Math.random() * 900000000);
      toast.warning("Organizer tidak ditemukan, menggunakan data default");
    }
  }

  private updateFormattedDates() {
    if (this.startDate) {
      this.formattedStart = this.formatDate(this.startDate);
    } else {
      this.formattedStart = "";
    }

    if (this.endDate) {
      this.formattedEnd = this.formatDate(this.endDate);
    } else {
      this.formattedEnd = "";
    }
  }

  private formatDate(dateString: string): string {
    if (!dateString) return "";

    const date = new Date(dateString);
    const day = date.getDate();
    const month = date.getMonth() + 1;
    const year = date.getFullYear();

    return `${day}/${month}/${year}`;
  }

  // Time handling
  private getTimeInput(event: Event, field: "startTime" | "endTime") {
    const input = event.target as HTMLInputElement;
    this[field] = input.value;
  }

  private validateEmail(email: string): boolean {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  }

  private validatePhone(phone: string): boolean {
    const cleaned = phone.replace(/\D/g, "");
    return cleaned.length >= 10 && cleaned.length <= 14;
  }

  private validateDomain(domain: string): boolean {
    return /^[a-z0-9-]+$/.test(domain);
  }

  private nextStep() {
    if (!this.eventName) {
      toast.warning("Nama acara harus diisi");
      return;
    }

    if (!this.subdomain) {
      toast.warning("Subdomain harus diisi");
      return;
    }

    if (!this.validateDomain(this.subdomain)) {
      toast.warning("Nama domain tidak boleh mengandung spasi dan simbol");
      return;
    }

    if (!this.description.trim()) {
      toast.warning("Deskripsi acara harus diisi");
      return;
    }

    if (this.wordCount > 500) {
      toast.warning("Deskripsi tidak boleh melebihi 500 kata");
      return;
    }

    if (!this.startDate) {
      toast.warning("Tanggal mulai harus diisi");
      return;
    }

    if (!this.endDate) {
      toast.warning("Tanggal selesai harus diisi");
      return;
    }

    if (new Date(this.endDate) < new Date(this.startDate)) {
      toast.warning("Tanggal selesai tidak boleh sebelum tanggal mulai");
      return;
    }

    if (!this.startTime) {
      toast.warning("Waktu mulai harus diisi");
      return;
    }

    if (!this.endTime) {
      toast.warning("Waktu selesai harus diisi");
      return;
    }

    if (!this.banner) {
      toast.warning("Masukkan banner event");
      return;
    } else {
      let file = this.banner;
      let _URL = window.URL || window.webkitURL;
      let img = new Image();
      img.onload = function () {
        const width = img.width;
        const height = img.height;
        if (width < 1200 && height < 400) {
          toast.warning("Ukuran banner yang diperbolehkan minimal 1200x400 px");
          return;
        }
      };
      img.onerror = function () {
        toast.warning("File harus image");
        return;
      };
      img.src = _URL.createObjectURL(file);
    }

    if (!this.province?.id) {
      toast.warning("Provinsi harus dipilih");
      return;
    }

    if (!this.city?.id) {
      toast.warning("Kota harus dipilih");
      return;
    }

    if (!this.address) {
      toast.warning("Alamat harus diisi");
      return;
    }

    if (!this.eventTypes) {
      toast.warning("Tipe acara harus dipilih");
      return;
    }

    if (!this.organizerName) {
      toast.warning("Nama penyelenggara harus diisi");
      return;
    }

    if (!this.organizerEmail) {
      toast.warning("Email penyelenggara harus diisi");
      return;
    }

    if (!this.validateEmail(this.organizerEmail)) {
      toast.warning("Format email penyelenggara tidak valid");
      return;
    }

    if (!this.organizerPhone) {
      toast.warning("Nomor telepon penyelenggara harus diisi");
      return;
    }

    if (!this.validatePhone(this.organizerPhone)) {
      toast.warning("Nomor telepon penyelenggara tidak valid");
      return;
    }

    // Validasi PICs
    for (let i = 0; i < this.pics.length; i++) {
      const pic = this.pics[i];
      if (!pic.name) {
        toast.warning(`Nama PIC ke-${i + 1} harus diisi`);
        return;
      }
      if (!pic.position) {
        toast.warning(`Jabatan PIC ke-${i + 1} harus diisi`);
        return;
      }
      if (!pic.email) {
        toast.warning(`Email PIC ke-${i + 1} harus diisi`);
        return;
      }
      if (!this.validateEmail(pic.email)) {
        toast.warning(`Format email PIC ke-${i + 1} tidak valid`);
        return;
      }
      if (!pic.phone) {
        toast.warning(`Nomor telepon PIC ke-${i + 1} harus diisi`);
        return;
      }
      if (!this.validatePhone(pic.phone)) {
        toast.warning(`Nomor telepon PIC ke-${i + 1} tidak valid`);
        return;
      }
    }

    this.submitJobFair();
  }

  private async submitJobFair() {
    try {
      const formData = new FormData();
      if (typeof this.$route.query.token === "string")
        formData.append("sales_token", this.$route.query.token);
      else
        return toast.warning(
          `Parameter tidak valid!. Token registrasi diperlukan`
        );
      formData.append("name", this.eventName);
      formData.append("slug", this.subdomain);
      formData.append("description", this.description);
      formData.append("start_date", `${this.startDate} ${this.startTime}`);
      formData.append("end_date", `${this.endDate} ${this.startTime}`);
      if (this.banner) formData.append("image", this.banner);
      else
        return toast.warning(
          `Parameter tidak valid!. Token registrasi diperlukan`
        );
      formData.append("province_id", this.province.id);
      formData.append("city_id", this.city.id);
      formData.append("location", this.address);
      formData.append("type", this.eventType);
      formData.append("organizer_name", this.organizerName);
      formData.append("organizer_email", this.organizerEmail);
      formData.append("organizer_phone", this.organizerPhone);
      formData.append("organizer_logo", this.organizerLogo || "");

      this.pics.forEach((item, index) =>
        Object.entries(item).forEach(([key, value]) =>
          formData.append(`organizer_admins[${index}][${key}]`, value)
        )
      );

      await this.registerJobfairSales({
        params: formData,
        success: () => {
          this.step = 2;
          window.scrollTo(0, 0);
        },
        fail: (error: any) => {
          toast.error(
            error.message || "Terjadi kesalahan saat mendaftarkan acara"
          );
        },
      });
    } catch (error) {
      toast.error("Terjadi kesalahan saat memproses formulir");
    }
  }
}
